import React from 'react';
import {
  View,
  Text,
  ScrollView,
  Image,
  TouchableOpacity,
  StyleSheet,
  ActivityIndicator,
} from 'react-native';
import Feather from '@expo/vector-icons/Feather';
import { Paprika, PaprikaTypography, PaprikaRadius } from '../../styles/theme';
import { ExtractedPhotoRecipe } from '../../types/photoImport';
import { useTranslation } from 'react-i18next';

const AMBER = '#D97706';

interface PhotoRecipeReviewScreenProps {
  imageUri: string;
  recipe: ExtractedPhotoRecipe | null;
  confidence: number;
  isSaving: boolean;
  onSave: () => void;
  onEdit: () => void;
  onRetake: () => void;
  onChooseAnother: () => void;
}

export default function PhotoRecipeReviewScreen({
  imageUri,
  recipe,
  confidence,
  isSaving,
  onSave,
  onEdit,
  onRetake,
  onChooseAnother,
}: PhotoRecipeReviewScreenProps) {
  const { t } = useTranslation();
  const showWarning = recipe !== null && confidence < 60;

  return (
    <View style={styles.container}>
      <ScrollView style={styles.scroll} contentContainerStyle={styles.scrollContent} showsVerticalScrollIndicator={false}>
        {/* Photo panel */}
        <View style={styles.photoPanel}>
          <Image source={{ uri: imageUri }} style={styles.photo} resizeMode="cover" />
          {recipe !== null && (
            <>
              <View style={[styles.regionOverlay, styles.regionTitle]} pointerEvents="none" />
              <View style={[styles.regionOverlay, styles.regionIngredients]} pointerEvents="none" />
              <View style={[styles.regionOverlay, styles.regionInstructions]} pointerEvents="none" />
            </>
          )}
        </View>

        {/* Error state — no recipe extracted */}
        {recipe === null && (
          <View style={styles.errorState}>
            <View style={styles.errorIconWrap}>
              <Feather name="camera" size={28} color={AMBER} />
            </View>
            <Text style={styles.errorTitle}>{t('photoImport.couldNotRead')}</Text>
            <Text style={styles.errorBody}>{t('photoImport.blurry')}</Text>
            <View style={styles.errorActions}>
              <TouchableOpacity style={styles.retakeButton} onPress={onRetake}>
                <Feather name="camera" size={16} color={Paprika.surfaceLowest} />
                <Text style={styles.retakeButtonText}>{t('photoImport.retakePhoto')}</Text>
              </TouchableOpacity>
              <TouchableOpacity style={styles.chooseButton} onPress={onChooseAnother}>
                <Feather name="image" size={16} color={Paprika.primary} />
                <Text style={styles.chooseButtonText}>{t('photoImport.chooseAnother')}</Text>
              </TouchableOpacity>
            </View>
          </View>
        )}

        {recipe !== null && (
          <>
            {/* Confidence bar */}
            <View style={styles.confidenceRow}>
              <Text style={styles.confidenceLabel}>{t('photoImport.confidence')}</Text>
              <View style={styles.confidenceTrack}>
                <View style={{ flex: confidence, backgroundColor: Paprika.primary, borderRadius: 4 }} />
                <View style={{ flex: 100 - confidence, backgroundColor: Paprika.background }} />
              </View>
              <Text style={styles.confidencePct}>{confidence}%</Text>
            </View>

            {/* Low-confidence warning */}
            {showWarning && (
              <View style={styles.warningBanner}>
                <Feather name="alert-triangle" size={14} color={AMBER} />
                <Text style={styles.warningText}>{t('photoImport.incompleteWarning')}</Text>
              </View>
            )}

            {/* Title card */}
            <View style={styles.card}>
              <View style={styles.cardHeader}>
                <View style={[styles.dot, { backgroundColor: Paprika.primary }]} />
                <Text style={styles.cardLabel}>{t('photoImport.titleLabel')}</Text>
              </View>
              <Text style={styles.recipeTitle}>{recipe.title}</Text>
              {recipe.cuisine ? (
                <Text style={styles.cuisineTag}>{recipe.cuisine}</Text>
              ) : null}
            </View>

            {/* Details row */}
            {(recipe.prepTime || recipe.cookTime || recipe.servings) ? (
              <View style={styles.detailsRow}>
                {recipe.prepTime ? (
                  <View style={styles.chip}>
                    <Feather name="clock" size={12} color={Paprika.textSecondary} />
                    <Text style={styles.chipText}>{t('photoImport.prepMinutes', { minutes: recipe.prepTime })}</Text>
                  </View>
                ) : null}
                {recipe.cookTime ? (
                  <View style={styles.chip}>
                    <Feather name="thermometer" size={12} color={Paprika.textSecondary} />
                    <Text style={styles.chipText}>{t('photoImport.cookMinutes', { minutes: recipe.cookTime })}</Text>
                  </View>
                ) : null}
                {recipe.servings ? (
                  <View style={styles.chip}>
                    <Feather name="users" size={12} color={Paprika.textSecondary} />
                    <Text style={styles.chipText}>{t('photoImport.servings', { count: Number(recipe.servings) || 0 })}</Text>
                  </View>
                ) : null}
              </View>
            ) : null}

            {/* Ingredients card */}
            <View style={styles.card}>
              <View style={styles.cardHeader}>
                <View style={[styles.dot, { backgroundColor: AMBER }]} />
                <Text style={styles.cardLabel}>{t('photoImport.ingredientsCount', { count: recipe.ingredients.length })}</Text>
              </View>
              {recipe.ingredients.map((ing, i) => (
                <View key={i} style={styles.ingredientRow}>
                  <Text style={styles.ingredientQty}>{ing.quantity}</Text>
                  <Text style={styles.ingredientName}>{ing.name}</Text>
                </View>
              ))}
            </View>

            {/* Instructions card */}
            <View style={styles.card}>
              <View style={styles.cardHeader}>
                <View style={[styles.dot, { backgroundColor: Paprika.success }]} />
                <Text style={styles.cardLabel}>{t('photoImport.instructionsCount', { count: recipe.instructions.length })}</Text>
              </View>
              {recipe.instructions.map((inst, i) => (
                <View key={i} style={styles.instructionRow}>
                  <View style={styles.stepCircle}>
                    <Text style={styles.stepNumber}>{inst.step}</Text>
                  </View>
                  <Text style={styles.instructionText}>{inst.text}</Text>
                </View>
              ))}
            </View>

            <View style={styles.footerSpacer} />
          </>
        )}
      </ScrollView>

      {/* Fixed footer */}
      {recipe !== null && (
        <View style={styles.footer}>
          <TouchableOpacity
            style={[styles.saveButton, isSaving && styles.saveButtonDisabled]}
            onPress={onSave}
            disabled={isSaving}
          >
            {isSaving ? (
              <ActivityIndicator size="small" color={Paprika.surfaceLowest} />
            ) : (
              <Text style={styles.saveButtonText}>{t('photoImport.saveToPaprika')}</Text>
            )}
          </TouchableOpacity>
          <TouchableOpacity style={styles.editButton} onPress={onEdit} disabled={isSaving}>
            <Text style={styles.editButtonText}>{t('photoImport.editBeforeSaving')}</Text>
          </TouchableOpacity>
        </View>
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: Paprika.background,
  },
  scroll: {
    flex: 1,
  },
  scrollContent: {
    paddingBottom: 24,
  },
  photoPanel: {
    position: 'relative',
    height: 240,
    backgroundColor: Paprika.surfaceHighest,
  },
  photo: {
    width: '100%',
    height: 240,
  },
  regionOverlay: {
    position: 'absolute',
    left: 8,
    right: 8,
    borderWidth: 2,
    borderRadius: 4,
  },
  regionTitle: {
    top: 8,
    height: 72,
    borderColor: Paprika.primary,
  },
  regionIngredients: {
    top: 88,
    height: 96,
    borderColor: AMBER,
  },
  regionInstructions: {
    top: 192,
    height: 40,
    borderColor: Paprika.success,
  },
  confidenceRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    paddingHorizontal: 16,
    paddingTop: 16,
    paddingBottom: 4,
  },
  confidenceLabel: {
    ...PaprikaTypography.labelSmall,
    color: Paprika.textSecondary,
    width: 72,
  },
  confidenceTrack: {
    flex: 1,
    height: 8,
    borderRadius: 4,
    flexDirection: 'row',
    overflow: 'hidden',
    backgroundColor: Paprika.surfaceHighest,
  },
  confidencePct: {
    ...PaprikaTypography.labelSmall,
    color: Paprika.textSecondary,
    width: 36,
    textAlign: 'right',
  },
  warningBanner: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    marginHorizontal: 16,
    marginTop: 8,
    padding: 12,
    backgroundColor: '#fef3c7',
    borderRadius: PaprikaRadius.md,
  },
  warningText: {
    ...PaprikaTypography.bodySmall,
    color: '#92400e',
    flex: 1,
  },
  card: {
    marginHorizontal: 16,
    marginTop: 16,
    padding: 16,
    backgroundColor: Paprika.surfaceLowest,
    borderRadius: PaprikaRadius.lg,
    shadowColor: 'rgba(82, 68, 60, 0.08)',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 1,
    shadowRadius: 8,
    elevation: 2,
  },
  cardHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    marginBottom: 10,
  },
  dot: {
    width: 8,
    height: 8,
    borderRadius: 4,
  },
  cardLabel: {
    ...PaprikaTypography.labelSmall,
    color: Paprika.textSecondary,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  },
  recipeTitle: {
    fontFamily: 'Newsreader-Italic',
    fontSize: 22,
    lineHeight: 28,
    color: Paprika.textPrimary,
  },
  cuisineTag: {
    ...PaprikaTypography.labelSmall,
    color: Paprika.textMuted,
    marginTop: 4,
  },
  detailsRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
    paddingHorizontal: 16,
    marginTop: 8,
  },
  chip: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    paddingHorizontal: 10,
    paddingVertical: 5,
    backgroundColor: Paprika.surfaceLowest,
    borderRadius: PaprikaRadius.full,
    shadowColor: 'rgba(82, 68, 60, 0.06)',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 1,
    shadowRadius: 4,
    elevation: 1,
  },
  chipText: {
    ...PaprikaTypography.labelSmall,
    color: Paprika.textSecondary,
  },
  ingredientRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 8,
    paddingVertical: 4,
  },
  ingredientQty: {
    ...PaprikaTypography.bodySmall,
    color: Paprika.primary,
    width: 72,
    flexShrink: 0,
  },
  ingredientName: {
    ...PaprikaTypography.bodySmall,
    color: Paprika.textPrimary,
    flex: 1,
  },
  instructionRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 10,
    paddingVertical: 6,
  },
  stepCircle: {
    width: 24,
    height: 24,
    borderRadius: 12,
    backgroundColor: Paprika.success,
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    marginTop: 1,
  },
  stepNumber: {
    fontFamily: 'Inter-SemiBold',
    fontSize: 11,
    color: Paprika.surfaceLowest,
  },
  instructionText: {
    ...PaprikaTypography.bodySmall,
    color: Paprika.textPrimary,
    flex: 1,
    lineHeight: 20,
  },
  footerSpacer: {
    height: 100,
  },
  footer: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    paddingHorizontal: 16,
    paddingTop: 12,
    paddingBottom: 32,
    backgroundColor: Paprika.background,
    borderTopWidth: 1,
    borderTopColor: Paprika.surfaceHighest,
    gap: 8,
  },
  saveButton: {
    backgroundColor: Paprika.primary,
    borderRadius: PaprikaRadius.md,
    paddingVertical: 14,
    alignItems: 'center',
    justifyContent: 'center',
  },
  saveButtonDisabled: {
    opacity: 0.6,
  },
  saveButtonText: {
    fontFamily: 'Inter-SemiBold',
    fontSize: 16,
    color: Paprika.surfaceLowest,
  },
  editButton: {
    borderRadius: PaprikaRadius.md,
    paddingVertical: 12,
    alignItems: 'center',
    justifyContent: 'center',
  },
  editButtonText: {
    fontFamily: 'Inter-Medium',
    fontSize: 15,
    color: Paprika.textSecondary,
  },
  errorState: {
    alignItems: 'center',
    paddingHorizontal: 32,
    paddingTop: 32,
    paddingBottom: 16,
  },
  errorIconWrap: {
    width: 64,
    height: 64,
    borderRadius: 32,
    backgroundColor: '#fef3c7',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 16,
  },
  errorTitle: {
    fontFamily: 'Newsreader-Italic',
    fontSize: 20,
    color: Paprika.textPrimary,
    marginBottom: 8,
    textAlign: 'center',
  },
  errorBody: {
    ...PaprikaTypography.bodySmall,
    color: Paprika.textSecondary,
    textAlign: 'center',
    lineHeight: 20,
    marginBottom: 24,
  },
  errorActions: {
    flexDirection: 'row',
    gap: 12,
  },
  retakeButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    backgroundColor: Paprika.primary,
    paddingHorizontal: 16,
    paddingVertical: 10,
    borderRadius: PaprikaRadius.md,
  },
  retakeButtonText: {
    fontFamily: 'Inter-SemiBold',
    fontSize: 14,
    color: Paprika.surfaceLowest,
  },
  chooseButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    backgroundColor: Paprika.surfaceLowest,
    paddingHorizontal: 16,
    paddingVertical: 10,
    borderRadius: PaprikaRadius.md,
    borderWidth: 1,
    borderColor: Paprika.primary,
  },
  chooseButtonText: {
    fontFamily: 'Inter-SemiBold',
    fontSize: 14,
    color: Paprika.primary,
  },
});
